/**
 * docsServiceFactory — the Docs Firestore layer, bound to a collection.
 *
 * WHY A FACTORY RATHER THAN A PARAMETER ON EVERY FUNCTION. Docs now exists in
 * more than one space (Work → `workDocs`, Personal → `personalDocs`), and those
 * are separate COLLECTIONS rather than a `space` field on one collection —
 * collection-level isolation is what makes a private space private (core.md: "a
 * space boundary is a collection wall"). Threading a collection argument through
 * twelve exported functions would have put the choice at every call site, where
 * it can be got wrong once and leak a personal doc into the Work list. Binding it
 * once, here, means a caller holding a service literally cannot address another
 * space's data.
 *
 * The behaviour below is unchanged from the original single-collection
 * DocsService — this is a move, not a rewrite. `DocsService.js` binds Work and
 * re-exports the identical public API, so no existing caller changed.
 *
 * AUTOSAVE SHAPE, deliberately: every save is a single last-write-wins overwrite
 * of ONE document. No arrayUnion, no per-keystroke appends. That is a direct
 * response to the repertoire session-inflation bug, where a debounced whole-array
 * save appended a new entry on every change because arrayUnion only dedupes
 * deep-equal values. One doc, overwritten, cannot inflate.
 *
 * Single-writer by design — no presence, no locking, no CRDT. At 2-3 staff the
 * realistic conflict is nil; if it ever bites, that's a later slice.
 *
 * PER-COLLECTION INDEXES. subscribeToDocs runs where(archived) + orderBy(updatedAt),
 * which needs a composite index — and a Firestore index belongs to ONE collection,
 * so every collection this factory is bound to needs its own copy declared in
 * firestore.indexes.json. Binding a new space without adding the index yields a
 * clean-looking service that throws the first time the list renders.
 */

import {
	collection,
	doc,
	addDoc,
	getDoc,
	getDocs,
	updateDoc,
	deleteDoc,
	onSnapshot,
	query,
	where,
	orderBy,
	limit as fsLimit,
	serverTimestamp,
	writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import { deriveDocFields } from './docsModel';
import { buildFromTemplate, DOC_TYPES } from './docsTemplates';
import {
	moveUpdates,
	promoteChildrenUpdates,
	descendantsOf,
	duplicateMappings,
} from './docsTree';

/* Firestore caps a batch at 500 writes. Chunk below that so moving a large
   subtree can't fail wholesale — each chunk commits independently. */
const BATCH_LIMIT = 450;

/**
 * @param {object} opts
 * @param {string} opts.collectionName Firestore collection this service addresses
 * @param {string} opts.space          value stamped on `space` for new documents
 */
export function createDocsService({ collectionName, space }) {
	if (!collectionName || !space) {
		// Both are required: a service bound to undefined would silently address
		// the wrong path, which for a private space is a data-leak shape rather
		// than a crash. Fail at construction instead.
		throw new Error('createDocsService requires { collectionName, space }');
	}

	const colRef = () => collection(db, collectionName);
	const docRef = (id) => doc(db, collectionName, id);

	/**
	 * Live subscription to the doc list. Matches the composite index
	 * (archived ASC, updatedAt DESC) declared in firestore.indexes.json — keep the
	 * query and the index in step or Firestore will reject it at runtime.
	 */
	function subscribeToDocs(onData, onError, { max = 200 } = {}) {
		const q = query(
			colRef(),
			where('archived', '==', false),
			orderBy('updatedAt', 'desc'),
			fsLimit(max)
		);
		return onSnapshot(
			q,
			(snap) => onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
			onError
		);
	}

	function subscribeToDoc(id, onData, onError) {
		return onSnapshot(
			docRef(id),
			(snap) => onData(snap.exists() ? { id: snap.id, ...snap.data() } : null),
			onError
		);
	}

	async function fetchDoc(id) {
		const snap = await getDoc(docRef(id));
		return snap.exists() ? { id: snap.id, ...snap.data() } : null;
	}

	/**
	 * Create a doc. Seeds an empty H1 so the editor opens ready to be titled —
	 * `title` stays null until the user actually types one (the list renders the
	 * untitled state; we never persist the literal word "Untitled").
	 */
	async function createDoc({
		uid,
		displayName,
		parentId = null,
		template = 'blank',
	} = {}) {
		const { content, type } = buildFromTemplate(template);
		// Title/plainText are DERIVED from the template content for the same reason
		// saves derive them — a paperwork template ships with an H1, so a new show
		// flow is titled the moment it exists rather than sitting as "Untitled".
		const derived = deriveDocFields(content);
		// ancestors[] is DERIVED from the parent — never passed in — so the two
		// representations of position can't disagree at creation time.
		let ancestors = [];
		if (parentId) {
			const parentSnap = await getDoc(docRef(parentId));
			ancestors = parentSnap.exists()
				? [...(parentSnap.data().ancestors || []), parentId]
				: [];
		}
		const ref = await addDoc(colRef(), {
			title: derived.title,
			content,
			plainText: derived.plainText,
			mentions: derived.mentions,
			type,
			// Crew reference documents by version — cue sheets change between rehearsal
			// and load-in, and "which one are you holding?" has to be answerable.
			version: type === DOC_TYPES.SHOW_FLOW ? 1 : null,
			tags: [],
			space,
			parentId: parentId || null,
			ancestors,
			archived: false,
			pinned: false,
			createdBy: uid || null,
			createdByName: displayName || null,
			createdAt: serverTimestamp(),
			updatedBy: uid || null,
			updatedAt: serverTimestamp(),
		});
		return ref.id;
	}

	/**
	 * Persist an edit. Title/plainText/mentions are all DERIVED here rather than
	 * passed in, so a caller can never write a title that disagrees with the H1 in
	 * the content it saved alongside it.
	 */
	async function saveDocContent(id, json, { uid } = {}) {
		const { title, plainText, mentions } = deriveDocFields(json);
		await updateDoc(docRef(id), {
			content: json,
			title,
			plainText,
			mentions,
			updatedBy: uid || null,
			updatedAt: serverTimestamp(),
		});
	}

	async function setDocPinned(id, pinned) {
		await updateDoc(docRef(id), { pinned: !!pinned, updatedAt: serverTimestamp() });
	}

	async function commitUpdates(updates) {
		for (let i = 0; i < updates.length; i += BATCH_LIMIT) {
			const batch = writeBatch(db);
			for (const u of updates.slice(i, i + BATCH_LIMIT)) {
				const { id, ...fields } = u;
				batch.update(docRef(id), { ...fields, updatedAt: serverTimestamp() });
			}
			await batch.commit();
		}
	}

	/** All docs, unfiltered — the tree math needs the whole set to resolve paths. */
	async function fetchAllForTree() {
		const snap = await getDocs(colRef());
		return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
	}

	/**
	 * Re-parent a doc and rewrite every descendant's ancestors[] in one pass.
	 *
	 * The cycle guard lives in docsTree.moveUpdates, which returns [] for an illegal
	 * move — so an attempt to drop a doc inside its own subtree is a no-op here, not
	 * a corrupted tree, even if a caller bypasses the UI.
	 */
	async function moveDoc(docId, newParentId) {
		const all = await fetchAllForTree();
		const updates = moveUpdates(docId, newParentId, all);
		if (!updates.length) return { moved: false, writes: 0 };
		await commitUpdates(updates);
		return { moved: true, writes: updates.length };
	}

	/** Valid move targets for a doc — everything that isn't itself or its own descendant. */
	async function fetchMoveTargets(docId) {
		const all = await fetchAllForTree();
		return all.filter(
			(d) => d.id !== docId && !(d.ancestors || []).includes(docId) && !d.archived
		);
	}

	/**
	 * Duplicate a doc AND everything beneath it.
	 *
	 * This is what makes user-authored templates possible without a template
	 * system: shape a show flow once, keep it as a master, duplicate it per show.
	 *
	 * Deep by design — duplicating a "Templates" folder that silently dropped its
	 * children would be worse than not offering it. Ids are minted CLIENT-SIDE
	 * (doc(colRef()) generates an id without writing), so the whole copy — parents
	 * and descendants, correctly re-pointed at each other — commits as one batch
	 * instead of a sequence of round-trips that could half-fail.
	 *
	 * The copy lands as a sibling of the original. Only the root copy is retitled.
	 */
	async function duplicateDoc(id, { uid, displayName } = {}) {
		const all = await fetchAllForTree();
		const source = all.find((d) => d.id === id);
		if (!source) return null;

		const subtree = [source, ...descendantsOf(all, id)];
		const idMap = new Map(subtree.map((d) => [d.id, doc(colRef()).id]));
		const plan = duplicateMappings(subtree, id, idMap);
		const byId = new Map(subtree.map((d) => [d.id, d]));

		const batch = writeBatch(db);
		for (const p of plan) {
			const src = byId.get(p.fromId);
			batch.set(docRef(p.newId), {
				title: p.isRoot ? `Copy of ${src.title || 'Untitled'}` : src.title ?? null,
				content: src.content ?? null,
				plainText: src.plainText ?? '',
				mentions: src.mentions ?? [],
				tags: src.tags ?? [],
				type: src.type ?? 'doc',
				version: src.version ?? null,
				space,
				parentId: p.parentId,
				ancestors: p.ancestors,
				archived: false,
				pinned: false,
				createdBy: uid || null,
				createdByName: displayName || null,
				createdAt: serverTimestamp(),
				updatedBy: uid || null,
				updatedAt: serverTimestamp(),
			});
		}
		await batch.commit();

		return { id: idMap.get(id), copied: plan.length };
	}

	/** Archive is the soft default — it drops out of the list query, recoverable. */
	async function archiveDoc(id) {
		// Promote children first: archiving a parent would otherwise strand its
		// subtree behind a doc the list no longer shows.
		const all = await fetchAllForTree();
		const promotions = promoteChildrenUpdates(id, all);
		if (promotions.length) await commitUpdates(promotions);
		await updateDoc(docRef(id), { archived: true, updatedAt: serverTimestamp() });
	}

	async function unarchiveDoc(id) {
		await updateDoc(docRef(id), { archived: false, updatedAt: serverTimestamp() });
	}

	/**
	 * Hard delete. Children are PROMOTED to this doc's parent, never deleted —
	 * losing a whole subtree because someone tidied one page would be a destructive
	 * surprise. The list UI confirms before calling this.
	 */
	async function deleteDocById(id) {
		const all = await fetchAllForTree();
		const promotions = promoteChildrenUpdates(id, all);
		if (promotions.length) await commitUpdates(promotions);
		await deleteDoc(docRef(id));
	}

	return {
		// Introspection — lets a test (or a debug surface) assert which space a
		// service instance is addressing without reaching into Firestore.
		collectionName,
		space,

		subscribeToDocs,
		subscribeToDoc,
		fetchDoc,
		createDoc,
		saveDocContent,
		setDocPinned,
		duplicateDoc,
		moveDoc,
		fetchMoveTargets,
		archiveDoc,
		unarchiveDoc,
		deleteDocById,
	};
}
